import { startOfMonth, subMonths } from 'date-fns';
import React, { useState } from 'react';

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

export enum Period {
  DAILY = 'DAILY',
  WEEKLY = 'WEEKLY',
  MONTHLY = 'MONTHLY',
}

export enum SortBy {
  REVENUE = 'REVENUE',
  QUANTITY = 'QUANTITY',
}

export interface DateRangeInput {
  startDate: string;
  endDate: string;
}

interface FilterProps {
  onChange: (filters: {
    period: Period;
    sortBy: SortBy;
    dateRange: DateRangeInput;
  }) => void;
}

function getLastMonthStart() {
  const now = new Date();
  return startOfMonth(subMonths(now, 180)).toISOString();
}
export function DashboardDataFilter({ onChange }: FilterProps) {
  const [period, setPeriod] = useState<Period>(Period.DAILY);
  const [sortBy, setSortBy] = useState<SortBy>(SortBy.REVENUE);
  const [dateRange, setDateRange] = useState<DateRangeInput>({
    startDate: getLastMonthStart(),
    endDate: new Date().toISOString(),
  });

  const handleChange = (key: keyof DateRangeInput, value: string) => {
    const updatedRange = { ...dateRange, [key]: value };
    setDateRange(updatedRange);
    onChange?.({ period, sortBy, dateRange: updatedRange });
  };

  const handleSelectChange = <T extends Period | SortBy>(
    type: 'period' | 'sortBy',
    value: T,
  ) => {
    if (type === 'period') {
      setPeriod(value as Period);
      onChange?.({ period: value as Period, sortBy, dateRange });
    } else {
      setSortBy(value as SortBy);
      onChange?.({ period, sortBy: value as SortBy, dateRange });
    }
  };

  return (
    <div className="grid gap-4 md:grid-cols-3">
      {/* Period Filter */}
      <div className="flex flex-col space-y-2">
        <Label>Period</Label>
        <Select
          value={period}
          onValueChange={(val) => handleSelectChange('period', val as Period)}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select period" />
          </SelectTrigger>
          <SelectContent>
            {Object.values(Period).map((p) => (
              <SelectItem key={p} value={p}>
                {p}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* SortBy Filter */}
      <div className="flex flex-col space-y-2">
        <Label>Sort By</Label>
        <Select
          value={sortBy}
          onValueChange={(val) => handleSelectChange('sortBy', val as SortBy)}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select sort" />
          </SelectTrigger>
          <SelectContent>
            {Object.values(SortBy).map((s) => (
              <SelectItem key={s} value={s}>
                {s}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Date Range */}
      <div className="flex flex-col space-y-2">
        <Label>Date Range</Label>
        <div className="flex space-x-2">
          <Input
            type="date"
            value={dateRange.startDate}
            onChange={(e) => handleChange('startDate', e.target.value)}
          />
          <Input
            type="date"
            value={dateRange.endDate}
            onChange={(e) => handleChange('endDate', e.target.value)}
            className="w-full"
          />
        </div>
      </div>
    </div>
  );
}
